import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    api.get(`/auth/verify-email?token=${token}`)
      .then((res) => {
        if (res.data?.verified) {
          setStatus('success');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        setStatus('error');
      });
  }, [token]);

  return (
    <div className="min-h-screen ept-bg-ambient flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-[#00F0FF]/5 rounded-full blur-[120px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass p-8 border border-[#00F0FF]/15 text-center max-w-md w-full relative z-10"
        style={{ boxShadow: '0 0 30px rgba(0,240,255,0.05)' }}
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-[#00F0FF] mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-xl font-bold text-white mb-2">
              {isItalian ? 'Verifica in corso...' : 'Verifying...'}
            </h2>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#00E676]/10 flex items-center justify-center border border-[#00E676]/20 mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
            </div>
            <h2 className="font-heading text-xl font-bold text-white mb-2" data-testid="verify-success-title">
              {isItalian ? 'Email Verificata!' : 'Email Verified!'}
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              {isItalian ? 'Il tuo account è stato attivato. Ora puoi accedere.' : 'Your account has been activated. You can now sign in.'}
            </p>
            <Button
              onClick={() => navigate('/login?verified=true')}
              data-testid="go-to-login-btn"
              className="btn-primary h-11 px-8 font-bold uppercase tracking-wider"
            >
              {isItalian ? 'Vai al Login' : 'Go to Login'}
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20 mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="font-heading text-xl font-bold text-white mb-2" data-testid="verify-error-title">
              {isItalian ? 'Link Non Valido' : 'Invalid Link'}
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              {isItalian ? 'Il link di verifica non è valido o è già stato utilizzato.' : 'The verification link is invalid or has already been used.'}
            </p>
            <Button
              onClick={() => navigate('/login')}
              data-testid="go-to-login-error-btn"
              className="btn-primary h-11 px-8 font-bold uppercase tracking-wider"
            >
              {isItalian ? 'Vai al Login' : 'Go to Login'}
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
